/**
 * Нормализация HTML для буфера обмена и нативное копирование.
 */

const ALLOWED_BLOCK = new Set(['p', 'br', 'ul', 'ol', 'li', 'div'])
const ALLOWED_INLINE = new Set(['strong', 'em', 'b', 'i', 'a', 'span'])
const ZERO_WIDTH = /[\u200B-\u200D\uFEFF\u00AD]/g

export function makeAbsoluteUrl(href, baseUrl) {
    if (!href || typeof href !== 'string') return ''
    const t = href.trim()
    if (!t) return ''
    if (/^https?:\/\//i.test(t) || /^mailto:/i.test(t) || /^tel:/i.test(t) || /^#/.test(t)) return t
    if (t.startsWith('//')) return 'https:' + t
    const base = (baseUrl || '').replace(/\/?$/, '/')
    if (t.startsWith('/')) return (base.replace(/\/$/, '') || '') + t
    return base + t
}

function getTextContent(node) {
    if (node.nodeType === Node.TEXT_NODE) return (node.textContent || '').replace(ZERO_WIDTH, '')
    if (node.nodeType !== Node.ELEMENT_NODE) return ''
    let out = ''
    for (const child of node.childNodes) out += getTextContent(child)
    return out.replace(ZERO_WIDTH, '')
}

function serializeNode(doc, node, baseUrl) {
    if (node.nodeType === Node.TEXT_NODE) {
        return (node.textContent || '').replace(ZERO_WIDTH, '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return ''

    const tag = (node.tagName || '').toLowerCase()
    if (tag === 'br') return '<br>'

    if (tag === 'a') {
        const href = node.getAttribute('href')
        const abs = makeAbsoluteUrl(href || '', baseUrl)
        const text = getTextContent(node)
        if (!abs) return text
        const safeHref = abs.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;')
        return '<a href="' + safeHref + '">' + (text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')) + '</a>'
    }

    if (ALLOWED_BLOCK.has(tag)) {
        const inner = []
        for (const child of node.childNodes) inner.push(serializeNode(doc, child, baseUrl))
        const innerStr = inner.join('')
        if (tag === 'div') return innerStr
        return '<' + tag + '>' + innerStr + '</' + tag + '>'
    }

    if (ALLOWED_INLINE.has(tag)) {
        const inner = []
        for (const child of node.childNodes) inner.push(serializeNode(doc, child, baseUrl))
        const innerStr = inner.join('')
        if (tag === 'span') return innerStr
        return '<' + tag + '>' + innerStr + '</' + tag + '>'
    }

    const inner = []
    for (const child of node.childNodes) inner.push(serializeNode(doc, child, baseUrl))
    return inner.join('')
}

export function normalizeClipboardHtml(html, baseUrl) {
    if (!html || typeof html !== 'string') return ''
    const base = baseUrl || (typeof window !== 'undefined' && window.location ? window.location.origin + '/' : '')

    const parser = typeof DOMParser !== 'undefined' ? new DOMParser() : null
    if (!parser) return html

    const doc = parser.parseFromString('<body>' + html + '</body>', 'text/html')
    const body = doc.body
    if (!body) return ''

    const parts = []
    for (const child of body.childNodes) parts.push(serializeNode(doc, child, base))
    let out = parts.join('')
    out = out.replace(/\n\s*\n\s*\n/g, '\n\n').replace(/^\s+|\s+$/g, '')
    return out
}

/**
 * Копирует rich text через перехват нативного события `copy`.
 *
 * Это единственный способ, при котором браузер гарантированно конвертирует
 * text/html → public.html в macOS pasteboard, как при обычном Ctrl+C.
 * Не зависит от ClipboardItem API.
 *
 * Метод:
 * 1. Вешаем одноразовый listener на событие `copy`
 * 2. В listener-е вызываем e.clipboardData.setData('text/html', html) и setData('text/plain', text)
 * 3. Вызываем e.preventDefault() чтобы браузер использовал наши данные
 * 4. Вызываем document.execCommand('copy') чтобы спровоцировать событие copy
 * 5. Listener срабатывает, данные попадают в системный буфер
 * 6. Убираем listener
 *
 * @param {string} html - нормализованный HTML
 * @param {string} plainText - обычный текст
 * @returns {boolean} true если execCommand вернул true
 */
export function copyViaClipboardEvent(html, plainText) {
    if (typeof document === 'undefined' || typeof document.execCommand !== 'function') return false
    let fired = false
    function handler(e) {
        e.clipboardData.setData('text/html', html)
        e.clipboardData.setData('text/plain', plainText)
        e.preventDefault()
        fired = true
    }

    document.addEventListener('copy', handler, true)
    let result = false
    try {
        result = document.execCommand('copy')
    } finally {
        document.removeEventListener('copy', handler, true)
    }
    return result && fired
}

/**
 * Текстовая версия HTML-фрагмента (без тегов), для text/plain в буфере.
 */
export function htmlFragmentToPlainText(htmlString) {
    if (!htmlString || typeof htmlString !== 'string') return ''
    const parser = typeof DOMParser !== 'undefined' ? new DOMParser() : null
    if (parser) {
        const doc = parser.parseFromString(htmlString, 'text/html')
        const body = doc.body
        if (body) {
            const parts = []
            for (const node of Array.from(body.childNodes || [])) {
                if (node.nodeType === Node.TEXT_NODE) {
                    const t = (node.textContent || '').replace(/\u00a0/g, ' ').trim()
                    if (t) parts.push(t)
                    continue
                }
                if (node.nodeType !== Node.ELEMENT_NODE) continue

                const el = /** @type {HTMLElement} */ (node)
                const tag = el.tagName

                if (tag === 'P' || tag === 'DIV' || tag === 'LI') {
                    const t = ((el.innerText || el.textContent || '') + '').replace(/\u00a0/g, ' ').trim()
                    parts.push(t)
                    continue
                }

                if (tag === 'UL' || tag === 'OL') {
                    const items = Array.from(el.querySelectorAll(':scope > li'))
                    for (const li of items) {
                        const t = ((li.innerText || li.textContent || '') + '').replace(/\u00a0/g, ' ').trim()
                        parts.push(t)
                    }
                    continue
                }

                const t = ((el.innerText || el.textContent || '') + '').replace(/\u00a0/g, ' ').trim()
                if (t) parts.push(t)
            }
            return parts.join('\n\n').replace(/\n{3,}/g, '\n\n').replace(/^\s+|\s+$/g, '')
        }
    }
    if (typeof document !== 'undefined') {
        const el = document.createElement('div')
        el.innerHTML = htmlString
        return (el.innerText || el.textContent || '').replace(/\u00a0/g, ' ')
    }
    return htmlString.replace(/<[^>]*>/g, '')
}

/**
 * Удаляет document-обёртки/мета и оставляет только HTML-фрагмент (body innerHTML).
 */
export function toClipboardHtmlFragment(htmlString) {
    if (!htmlString || typeof htmlString !== 'string') return ''
    const parser = typeof DOMParser !== 'undefined' ? new DOMParser() : null
    if (!parser) return htmlString.replace(/<meta[\s\S]*?>/gi, '').trim()
    const doc = parser.parseFromString(htmlString, 'text/html')
    const body = doc.body
    if (!body) return htmlString.replace(/<meta[\s\S]*?>/gi, '').trim()
    let out = (body.innerHTML || '').replace(/<meta[\s\S]*?>/gi, '').trim()
    out = out.replace(/<p>\s*<br\s*\/?>\s*<\/p>/gi, '<br>')
    return out.trim()
}

/** Чтение Blob в UTF-8 (FileReader — надёжно в jsdom/Jest, где Blob#text пустой). */
export async function readBlobAsUtf8(blob) {
    if (!blob) return ''
    if (typeof FileReader !== 'undefined') {
        try {
            const text = await new Promise((resolve, reject) => {
                const fr = new FileReader()
                fr.onload = () => resolve(fr.result)
                fr.onerror = () => reject(new Error('FileReader'))
                fr.readAsText(blob, 'utf-8')
            })
            if (typeof text === 'string' && (text.length > 0 || !blob.size)) return text
        } catch (e) {
            /* fall through */
        }
    }
    try {
        if (typeof Response !== 'undefined') {
            const viaResponse = await new Response(blob).text()
            if (viaResponse.length > 0 || !blob.size) return viaResponse
        }
    } catch (e) {
        /* fall through */
    }
    if (typeof blob.text === 'function') {
        try {
            return await blob.text()
        } catch (e) {
            /* fall through */
        }
    }
    if (typeof blob.arrayBuffer === 'function') {
        try {
            const buf = await blob.arrayBuffer()
            return new TextDecoder('utf-8').decode(buf)
        } catch (e) {
            /* fall through */
        }
    }
    return ''
}

function copyHtmlViaContentEditableSelection(htmlString) {
    if (typeof document === 'undefined' || !document.body || typeof document.execCommand !== 'function') return false
    const el = document.createElement('div')
    el.setAttribute('contenteditable', 'true')
    el.setAttribute('tabindex', '-1')
    el.style.position = 'fixed'
    el.style.left = '-9999px'
    el.style.top = '0'
    el.style.opacity = '0'
    el.innerHTML = htmlString
    document.body.appendChild(el)
    const selection = window.getSelection()
    const range = document.createRange()
    range.selectNodeContents(el)
    selection.removeAllRanges()
    selection.addRange(range)
    let ok = false
    try {
        ok = document.execCommand('copy')
    } finally {
        selection.removeAllRanges()
        document.body.removeChild(el)
    }
    return ok
}

/**
 * Копирует HTML-фрагмент в буфер как text/html и text/plain (Telegram, Notes и т.д.).
 * Порядок: copy-event interception → Async Clipboard API → contenteditable + copy.
 *
 * @param {string} htmlString — валидный фрагмент, напр. <p>...<a href="...">...</a>...</p>
 * @returns {Promise<boolean>}
 */
export async function copyHtmlToClipboard(htmlString) {
    if (!htmlString || typeof htmlString !== 'string' || !htmlString.trim()) return false

    const htmlFragment = toClipboardHtmlFragment(htmlString)
    if (!htmlFragment) return false
    const plainText = htmlFragmentToPlainText(htmlFragment)
    const htmlLength = htmlFragment.length
    const plainLength = plainText.length

    const canAsyncClipboard =
        typeof navigator !== 'undefined' &&
        navigator.clipboard &&
        typeof navigator.clipboard.write === 'function' &&
        typeof ClipboardItem !== 'undefined'

    const htmlHasEmptyPBr = /<p>\s*<br\s*\/?>\s*<\/p>/i.test(htmlFragment)
    const plainHasDoubleNewlines = plainText.includes('\n\n')
    console.log('[clipboard-runtime]', {
        writtenTypes: ['text/html', 'text/plain'],
        html200: htmlFragment.slice(0, 200),
        plain200: plainText.slice(0, 200),
        htmlHasEmptyPBr,
        plainHasDoubleNewlines
    })

    // Для Telegram и ряда desktop-приложений надёжнее сначала нативный copy-event путь.
    if (copyViaClipboardEvent(htmlFragment, plainText)) {
        console.log('Clipboard write success', { htmlLength, plainLength })
        return true
    }

    if (canAsyncClipboard) {
        const BlobCtor =
            (typeof document !== 'undefined' &&
                document.defaultView &&
                document.defaultView.Blob) ||
            (typeof window !== 'undefined' && window.Blob) ||
            (typeof Blob !== 'undefined' ? Blob : undefined)
        if (BlobCtor) {
            try {
                await navigator.clipboard.write([
                    new ClipboardItem({
                        'text/html': new BlobCtor([htmlFragment], { type: 'text/html' }),
                        'text/plain': new BlobCtor([plainText], { type: 'text/plain' })
                    })
                ])
                console.log('Clipboard write success', { htmlLength, plainLength })
                return true
            } catch (e) {
                console.warn('Clipboard API write failed, trying fallbacks:', e)
            }
        }
    }

    if (copyHtmlViaContentEditableSelection(htmlFragment)) {
        console.log('Clipboard write success', { htmlLength, plainLength })
        return true
    }

    console.warn('copyHtmlToClipboard: all methods failed')
    return false
}

/**
 * Отладка: какие MIME-типы в буфере и начало содержимого.
 */
export async function debugReadClipboard() {
    if (typeof navigator === 'undefined' || !navigator.clipboard || typeof navigator.clipboard.read !== 'function') {
        console.log('debugReadClipboard: navigator.clipboard.read недоступен')
        return
    }
    try {
        const items = await navigator.clipboard.read()
        console.log('debugReadClipboard: число ClipboardItem:', items.length)
        for (let i = 0; i < items.length; i++) {
            const item = items[i]
            const types = item.types || []
            console.log(`debugReadClipboard: item[${i}] MIME:`, [...types])
            for (const mime of types) {
                const blob = await item.getType(mime)
                const text = await readBlobAsUtf8(blob)
                console.log(`debugReadClipboard: [${mime}] len=${text.length}`, text.slice(0, 500))
            }
        }
    } catch (e) {
        console.warn('debugReadClipboard: ошибка чтения (нужны разрешения / HTTPS):', e)
    }
}

/**
 * Читает текущий буфер и печатает краткий отчёт о форматах/содержимом.
 * Ничего не записывает в буфер.
 *
 * @param {string} [hrefNeedle] Пустая строка — не проверять префикс href в HTML (любая ссылка ок).
 * @returns {Promise<{ ok: boolean, details?: object, skippedRead?: boolean }>}
 */
export async function verifyCurrentClipboardFormats(hrefNeedle = '') {
    if (typeof navigator === 'undefined' || !navigator.clipboard || typeof navigator.clipboard.read !== 'function') {
        console.log('verifyCurrentClipboardFormats: read() недоступен, пропуск проверки')
        return { ok: true, skippedRead: true }
    }

    try {
        const items = await navigator.clipboard.read()
        const diagnostics = []
        let htmlContent = ''
        let plainContent = ''
        let hasHtml = false
        let hasPlain = false

        for (const item of items) {
            const types = item.types || []
            for (const mime of types) {
                const blob = await item.getType(mime)
                const text = await readBlobAsUtf8(blob)
                diagnostics.push({ mime, length: text.length, preview200: text.slice(0, 200) })
                if (mime === 'text/html') {
                    hasHtml = true
                    htmlContent = text
                }
                if (mime === 'text/plain') {
                    hasPlain = true
                    plainContent = text
                }
            }
        }

        const hrefOk = hrefNeedle ? htmlContent.includes(`<a href="${hrefNeedle}`) : true
        const plainNoTags = !/<[a-z][\s\S]*?>/i.test(plainContent)
        const ok = hasHtml && hasPlain && hrefOk && plainNoTags

        if (!ok) {
            console.error('verifyCurrentClipboardFormats: проверка НЕ пройдена', {
                hasHtml,
                hasPlain,
                hrefOk,
                plainNoTags,
                mimeTypes: diagnostics.map((d) => d.mime)
            })
            for (const d of diagnostics) {
                console.error(`  [${d.mime}] len=${d.length}`, d.preview200)
            }
        } else {
            console.log('verifyCurrentClipboardFormats: OK', { hasHtml, hasPlain, hrefOk, plainNoTags })
        }

        return {
            ok,
            details: { hasHtml, hasPlain, hrefOk, plainNoTags, diagnostics }
        }
    } catch (e) {
        console.warn('verifyCurrentClipboardFormats: read() ошибка:', e)
        return { ok: false, reason: 'read failed', error: e }
    }
}

/** HTML для ручной проверки вставки (Telegram, Notes, Google Docs). */
export const CLIPBOARD_MANUAL_TEST_HTML =
    '<p>в компании. <a href="https://forms.gle/aKsXvG7ZrikKKLJr9">Оставить отзыв</a>.</p>'

/**
 * Копирует образец, читает буфер через read(), проверяет MIME и содержимое.
 * При сбое выводит фактические типы и первые 200 символов каждого типа.
 *
 * @param {string} [htmlString]
 * @returns {Promise<{ ok: boolean, details?: object }>}
 */
export async function runClipboardFormatVerification(htmlString = CLIPBOARD_MANUAL_TEST_HTML) {
    const copied = await copyHtmlToClipboard(htmlString)
    if (!copied) {
        console.error('runClipboardFormatVerification: копирование не удалось')
        return { ok: false, reason: 'copy failed' }
    }

    if (typeof navigator === 'undefined' || !navigator.clipboard || typeof navigator.clipboard.read !== 'function') {
        console.log('runClipboardFormatVerification: read() недоступен, пропуск проверки чтения')
        return { ok: true, skippedRead: true }
    }

    try {
        const items = await navigator.clipboard.read()
        const diagnostics = []
        let htmlContent = ''
        let plainContent = ''
        let hasHtml = false
        let hasPlain = false

        for (const item of items) {
            const types = item.types || []
            for (const mime of types) {
                const blob = await item.getType(mime)
                const text = await readBlobAsUtf8(blob)
                diagnostics.push({ mime, length: text.length, preview200: text.slice(0, 200) })
                if (mime === 'text/html') {
                    hasHtml = true
                    htmlContent = text
                }
                if (mime === 'text/plain') {
                    hasPlain = true
                    plainContent = text
                }
            }
        }

        const hrefOk = htmlContent.includes('<a href="https://forms.gle/')
        const plainNoTags = !/<[a-z][\s\S]*?>/i.test(plainContent)
        const ok = hasHtml && hasPlain && hrefOk && plainNoTags

        if (!ok) {
            console.error('runClipboardFormatVerification: проверка НЕ пройдена', {
                hasHtml,
                hasPlain,
                hrefOk,
                plainNoTags,
                mimeTypes: diagnostics.map((d) => d.mime)
            })
            for (const d of diagnostics) {
                console.error(`  [${d.mime}] len=${d.length}`, d.preview200)
            }
        } else {
            console.log('runClipboardFormatVerification: OK', { hasHtml, hasPlain, hrefOk, plainNoTags })
        }

        return {
            ok,
            details: { hasHtml, hasPlain, hrefOk, plainNoTags, diagnostics }
        }
    } catch (e) {
        console.warn('runClipboardFormatVerification: read() ошибка:', e)
        return { ok: false, reason: 'read failed', error: e }
    }
}
